import type {
  AutocompleteRenderInputParams,
  TextFieldProps,
} from "@mui/material";
import { TextField } from "@mui/material";

/**
 * MUI v6 deprecated `InputProps` / `inputProps` / `InputLabelProps` in favor
 * of `slotProps`, but `Autocomplete.renderInput` still hands them back in the
 * legacy shape. This wrapper translates so call sites stay clean. The final
 * cast to `TextFieldProps` sidesteps an exactOptionalPropertyTypes friction
 * between the Autocomplete-provided props (where `size: 'small' | undefined`)
 * and TextField's stricter `size?: 'small' | 'medium'`. MUI 7 is expected to
 * fix this; revisit on upgrade.
 */
export function AutocompleteInput({
  params,
  label,
  shrink,
}: {
  params: AutocompleteRenderInputParams;
  label: string;
  /**
   * Force the floating label into its top-left position even when the
   * input is empty. Opt-in (default = MUI's placeholder-style behaviour)
   * because the analysis-surface consumers (DefectsPanel /
   * FindingsPanel) intentionally want the label-as-placeholder look.
   * Toolbar filter pills (/data-acquisitions Status, 2026-05-13) pass
   * `shrink` so the label sits in the corner like Project / Site /
   * Assignee.
   */
  shrink?: boolean;
}) {
  const { InputLabelProps, InputProps, inputProps, ...rest } = params;
  const tfProps = {
    ...rest,
    label,
    slotProps: {
      htmlInput: inputProps,
      input: InputProps,
      inputLabel:
        shrink === true
          ? { ...InputLabelProps, shrink: true }
          : InputLabelProps,
    },
  } as unknown as TextFieldProps;
  return <TextField {...tfProps} />;
}
